import React, { useState, useEffect } from 'react';
import { Project, Change, User, Board } from '../../models';
import type { ColumnData } from '../../models';
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from '../../redux';
import { cancelCreateChange, createChange } from '../../redux';
import { loadBoard, updateBoard, loadChange } from '../../redux';
import { ChangeModal, PinnedChangePanel } from '../../components';
import type { BoardFilters } from '../../models';
import { KanBan } from '../KanBan';
import BoardSkeleton from './BoardSkeleton';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';

import { openChangeInNewBrowserTab } from '../../utils/openChangeInNewBrowserTab';

import './ProjectBoard.css';

const MAX_PINNED_CHANGES = 4;

/** Panel width + board/modal right offset per number of visible pins. */
function pinnedLayout(count: number): { panelClass: string; railClass: string } {
    if (count >= 4) {
        return {
            panelClass: 'w-[min(22rem,28vw)] max-w-none',
            railClass: 'md:pr-[min(88rem,96vw)]',
        };
    }
    if (count === 3) {
        return {
            panelClass: 'w-[min(26rem,32vw)] max-w-none',
            railClass: 'md:pr-[min(78rem,92vw)]',
        };
    }
    if (count === 2) {
        return {
            panelClass: 'w-[min(30rem,40vw)] max-w-none',
            railClass: 'md:pr-[min(60rem,82vw)]',
        };
    }
    // 1 pin — previous single-panel width (max-w-lg / 32rem)
    return {
        panelClass: 'w-[min(32rem,42vw)] max-w-none',
        railClass: 'md:pr-[min(32rem,42vw)]',
    };
}

interface ProjectBoardProps {
    project: Project;
    boardId: string;
    changeId?: string;
    user?: User;
    filters?: BoardFilters;
    showList?: boolean;
    compactList?: boolean;
    listSort?: 'newest' | 'oldest' | 'priority';
    onLabelClick?: (labelId: string) => void;
    hideLabelNames?: boolean;
}

function ProjectBoard(props: ProjectBoardProps) {
    const { project, boardId, filters, showList, compactList, listSort, onLabelClick, hideLabelNames } = props;

    const [showModal, setShowModal] = useState(false);
    const [pinnedChangeIds, setPinnedChangeIds] = useState<string[]>([]);

    const { board } = useAppSelector(state => state.boards);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // Redux keeps the previous board until the new one loads — only render
    // KanBan when it matches the requested boardId.
    const boardReady = !!board && board._id === boardId;
    const showSkeleton = !boardReady;

    const visiblePinnedIds = pinnedChangeIds.filter((id) => !showModal || id !== props.changeId);
    const showPinPanel = visiblePinnedIds.length > 0;
    const { panelClass: pinPanelClass, railClass: pinRailWidthClass } = pinnedLayout(visiblePinnedIds.length);
    const showPinAction = pinnedChangeIds.length > 0;

    useEffect(() => {
        dispatch(loadBoard(boardId));
    }, [dispatch, boardId, project?.syncedAt]);

    useEffect(() => {
        setPinnedChangeIds([]);
    }, [boardId]);

    useEffect(() => {
        if (props.changeId) {
            dispatch(loadChange(props.changeId));
            return setShowModal(true);
        }
        setShowModal(false);
    }, [dispatch, props.changeId]);

    const navigateToChange = React.useCallback((changeId: string) => {
        navigate(Project.path(board?.project || project, 'change', changeId), {
            state: { fromBoard: true },
        });
    }, [board?.project, project, navigate]);

    const closeModal = React.useCallback(() => {
        dispatch(cancelCreateChange());
        navigate(Project.path(board?.project));
    }, [board?.project, dispatch, navigate]);

    const onTogglePin = React.useCallback(() => {
        if (!props.changeId)
            return;
        if (pinnedChangeIds.includes(props.changeId)) {
            setPinnedChangeIds((prev) => prev.filter((id) => id !== props.changeId));
            return;
        }
        setPinnedChangeIds((prev) => {
            const without = prev.filter((id) => id !== props.changeId);
            const next = [...without, props.changeId as string];
            if (next.length <= MAX_PINNED_CHANGES)
                return next;
            return next.slice(next.length - MAX_PINNED_CHANGES);
        });
        closeModal();
    }, [props.changeId, pinnedChangeIds, closeModal]);

    const onTogglePinCard = React.useCallback((change: Change) => {
        if (!change?._id)
            return;
        if (pinnedChangeIds.includes(change._id)) {
            setPinnedChangeIds((prev) => prev.filter((id) => id !== change._id));
            return;
        }
        setPinnedChangeIds((prev) => {
            const without = prev.filter((id) => id !== change._id);
            const next = [...without, change._id];
            if (next.length <= MAX_PINNED_CHANGES)
                return next;
            return next.slice(next.length - MAX_PINNED_CHANGES);
        });
    }, [pinnedChangeIds]);

    const onOpenInNewTab = React.useCallback((change: Change) => {
        openChangeInNewBrowserTab(change, board?.project || project);
    }, [board?.project, project]);

    const onAddColumn = React.useCallback((columnTitle: string) => {
        board.columns = Board.insertColumnBeforeFirstSmart(board.columns, {
            _id: uuidv4(),
            title: columnTitle,
            editable: true,
            acceptNewCards: true,
            changes: []
        });
        dispatch(updateBoard(board));
    }, [board?.updatedAt]);

    const onEditColumn = React.useCallback((updatedColumn: ColumnData) => {
        board.columns = board.columns.map((column: ColumnData) => {
            if (column._id === updatedColumn._id)
                return updatedColumn;

            return column;
        });
        dispatch(updateBoard(board));
    }, [board?.updatedAt]);

    const onDuplicateColumn = React.useCallback((column: ColumnData) => {
        board.columns.push({
            ...column,
            _id: uuidv4(),
            title: `${column.title} - copy`,
            changes: []
        });
        dispatch(updateBoard(board));
    }, [board?.updatedAt]);

    const onRemoveColumn = React.useCallback((column: ColumnData) => {
        board.columns = board.columns.filter((c: ColumnData) => c._id !== column._id);
        dispatch(updateBoard(board));
    }, [board?.updatedAt]);

    const onEditCard = React.useCallback((change: Change) => {
        if (!change)
            return;
        navigate(Project.path(change.project || board?.project, 'change', change._id), {
            state: { fromBoard: true },
        });
    }, [board?.project?._id, navigate]);

    const onRemoveCard = React.useCallback((change: AnyObj) => {
        // Soft-delete is handled inside ChangeModal; here we only refresh UI.
        dispatch(cancelCreateChange());
        if (board?._id)
            dispatch(loadBoard(board._id));
        if (pinnedChangeIds.includes(change._id))
            setPinnedChangeIds((prev) => prev.filter((id) => id !== change._id));
        closeModal();
    }, [board, closeModal, dispatch, pinnedChangeIds]);

    const onCancelModal = React.useCallback(() => {
        closeModal();
    }, [closeModal]);

    const onAddCard = React.useCallback((values: AnyObj, boardId: string, columnId: string, showNew: boolean) => {
        values.project = board.project._id;
        const params = { boardId, columnId };
        dispatch(createChange(
            values,
            params,
            (err: Error | null, result?: AnyObj | AnyObj[] | null) => {
                dispatch(loadBoard(board._id));
                const change = result as Change;
                if (showNew && change)
                    navigate(Project.path(board.project, 'change', change._id), {
                        state: { fromBoard: true },
                    });
            }
        ));
    }, [board]);

    const changeMatchesFilters = React.useCallback((change: Change | string) => {
        if (!filters || typeof change === 'string')
            return true;

        const { user, search, status, labelId } = filters;
        if (!user && !search && !status && !labelId)
            return true;

        if (search && !change.match(search))
            return false;

        if (user && user?._id !== change.assignee?._id)
            return false;

        if (status && change.progress?.status !== status)
            return false;

        if (labelId && !(change.labelIds || []).includes(labelId))
            return false;

        return true;
    }, [filters?.search, filters?.user?._id, filters?.status, filters?.labelId]);

    const smartChangeIds = React.useMemo(
        () => Board.collectSmartChangeIds(board?.columns || []),
        [board?.updatedAt, board?.columns]
    );

    const changeIsVisible = React.useCallback((change: Change | string) => {
        if (!changeMatchesFilters(change))
            return false;

        const id = Board.changeId(change);
        return !id || !smartChangeIds.has(id);
    }, [changeMatchesFilters, smartChangeIds]);

    const commitOptimisticBoard = React.useCallback((columns: ColumnData[]) => {
        if (!board)
            return;

        const nextBoard = new Board({
            ...board,
            columns,
            updatedAt: new Date().toISOString(),
        });
        return dispatch(updateBoard(nextBoard, /*patch*/false, /*optimistic*/true));
    }, [board, dispatch]);

    const onDragCard = React.useCallback((result: AnyObj) => {
        const { type, source, destination, draggableId } = result;

        if (!destination || !board) {
            return;
        }

        if (type === 'column') {
            return commitOptimisticBoard(Board.reorderColumnsFromVisibleDrag(
                board.columns,
                source.index,
                destination.index,
                draggableId
            ));
        }

        const sourceColumnId = source.droppableId;
        const targetColumnId = destination.droppableId;

        if (sourceColumnId === targetColumnId && destination.index === source.index) {
            return;
        }

        if (sourceColumnId === targetColumnId) {
            return commitOptimisticBoard(board.columns.map((column: ColumnData) => {
                if (column._id !== sourceColumnId)
                    return column;

                return {
                    ...column,
                    changes: Board.reorderChangesFromVisibleDrag(
                        column.changes,
                        draggableId,
                        destination.index,
                        changeIsVisible
                    ) as Change[],
                };
            }));
        }

        const changes: Change[] = _.flatten(board.columns.map((column: ColumnData) => column.changes));
        const change: Change | undefined = changes?.find((c: Change) => Board.changeId(c) === draggableId);
        return commitOptimisticBoard(board.columns.map((column: ColumnData) => {
            if (column._id === sourceColumnId) {
                return {
                    ...column,
                    changes: column.changes.filter((c: Change) => Board.changeId(c) !== draggableId),
                };
            }

            if (column._id !== targetColumnId)
                return column;

            const item = (change || draggableId) as Change;
            const nextChanges = [...column.changes];
            const visible = nextChanges.filter(changeIsVisible);
            let insertAt = nextChanges.length;
            if (destination.index < visible.length) {
                const beforeId = Board.changeId(visible[destination.index]);
                insertAt = nextChanges.findIndex((c: Change) => Board.changeId(c) === beforeId);
            } else if (visible.length > 0) {
                const lastId = Board.changeId(visible[visible.length - 1]);
                insertAt = nextChanges.findIndex((c: Change) => Board.changeId(c) === lastId) + 1;
            }
            if (insertAt < 0)
                insertAt = nextChanges.length;
            nextChanges.splice(insertAt, 0, item);

            return {
                ...column,
                changes: nextChanges,
            };
        }));
    }, [board, changeIsVisible, commitOptimisticBoard]);

    return (
        <div className={`ProjectBoard w-full${showPinPanel ? ` ${pinRailWidthClass}` : ''}`}>
            {showSkeleton && (
                <BoardSkeleton showList={showList} compact={compactList} />
            )}
            {boardReady && (
                <div className='justify-stretch'>
                    <KanBan
                        board={board}
                        filters={filters}
                        onAddColumn={onAddColumn}
                        onEditColumn={onEditColumn}
                        onDuplicateColumn={onDuplicateColumn}
                        onRemoveColumn={onRemoveColumn}
                        onAddCard={onAddCard}
                        onEditCard={onEditCard}
                        onDragCard={onDragCard}
                        onLabelClick={onLabelClick}
                        hideLabelNames={hideLabelNames}
                        showList={showList}
                        compactList={compactList}
                        listSort={listSort}
                        showPinAction={showPinAction}
                        pinnedChangeIds={pinnedChangeIds}
                        onTogglePin={onTogglePinCard}
                        onOpenInNewTab={onOpenInNewTab}
                    />
                    <ChangeModal
                        project={project}
                        changeId={props.changeId}
                        show={showModal}
                        onClose={onCancelModal}
                        onRemove={onRemoveCard}
                        showProjectName={false}
                        sideOffset={showPinPanel}
                        sideOffsetClass={showPinPanel ? pinRailWidthClass : undefined}
                        isPinned={pinnedChangeIds.includes(props.changeId || '')}
                        onTogglePin={onTogglePin}
                    />
                    {showPinPanel && (
                        <div className="fixed top-0 right-0 z-[60] flex h-full">
                            {visiblePinnedIds.map((id) => (
                                <PinnedChangePanel
                                    key={id}
                                    changeId={id}
                                    project={project}
                                    className={pinPanelClass}
                                    onUnpin={() => setPinnedChangeIds((prev) => prev.filter((pinnedId) => pinnedId !== id))}
                                    onOpen={navigateToChange}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default ProjectBoard;
